import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { buildSearchSortQueryParams, parseSearchSortQueryParams } from '~/utils/listQueryParams';

/**
 * Search + column sort of a list, kept in the URL (`?search=…&sortBy=…&sortOrder=…`).
 *
 * The URL is the source of truth: `appliedSearch` and `sort` always come from it, so they survive a
 * reload, back/forward navigation and shared links. The page fetches in an effect that depends on
 * `queryString`; nothing here ever writes the URL on its own, only `updateUrl` does, from user actions.
 *
 * `updateUrl({ search, sort })` takes anything you leave out from the current URL and returns false
 * when the URL would not change (so the caller can refresh the list itself).
 *
 * @param {string[]} sortKeys allowed sort keys of the list (a module-level constant, see listQueryParams)
 */
const useListUrlQuery = (sortKeys) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: locationState } = useLocation();
  const queryString = searchParams.toString();

  const parsedQuery = useMemo(
    () => parseSearchSortQueryParams(new URLSearchParams(queryString), sortKeys),
    [queryString, sortKeys]
  );
  const { search: appliedSearch, sort } = parsedQuery;

  const updateUrl = ({ search: nextSearch = appliedSearch, sort: nextSort = sort }) => {
    const nextSearchParams = new URLSearchParams(
      buildSearchSortQueryParams({ search: nextSearch, sort: nextSort })
    );

    if (nextSearchParams.toString() === queryString) {
      return false;
    }

    // Keep the router state (e.g. the list query a history page returns to) across the URL update.
    setSearchParams(nextSearchParams, { replace: true, state: locationState });
    return true;
  };

  return { queryString, parsedQuery, appliedSearch, sort, updateUrl };
};

export default useListUrlQuery;
